import React, { useMemo, useState } from 'react';
import { BUCKET_ORDER } from 'shared/src/wrkthru/status-codes';
import type { PriorityBucket } from 'shared/src/wrkthru/status-codes';
import type { WrkthruItemType } from 'shared/src/wrkthru/completions-types';
import type {
  PortfolioStatusCounts,
  WrkthruIssue,
  WrkthruPagePayload,
  WrkthruTask
} from 'shared/src/wrkthru/types';
import { WrkthruHeader } from './wrkthru-header';
import { PortfolioStatusBar } from './portfolio-status-bar';
import { IssuesTabs } from './issues-tabs';
import type { IssuesTab } from './issues-tabs';
import { IssueSection } from './issue-section';
import { IssueCard } from './issue-card';
import { TaskCard } from './task-card';

type TodaysWrkPageProps = {
  payload: WrkthruPagePayload;
  onWrkthruIt?: (target: WrkthruIssue | WrkthruTask) => void;
  doneIssueIds: Set<string>;
  doneTaskIds: Set<string>;
  onMarkDone?: (itemType: WrkthruItemType, itemId: string) => void;
  onUndone?: (itemType: WrkthruItemType, itemId: string) => void;
  switcher?: React.ReactNode;
};

// P5 stays issue-only (it describes a healthy entity, not a unit of work).
const TASK_ALLOWED_BUCKETS: ReadonlyArray<PriorityBucket> = ['P1', 'P2', 'P3', 'P4'];

const byRankInBucket = (a: { rankInBucket?: number }, b: { rankInBucket?: number }) =>
  (a.rankInBucket ?? Number.MAX_SAFE_INTEGER) - (b.rankInBucket ?? Number.MAX_SAFE_INTEGER);

export function TodaysWrkPage({
  payload,
  onWrkthruIt,
  doneIssueIds,
  doneTaskIds,
  onMarkDone,
  onUndone,
  switcher
}: TodaysWrkPageProps) {
  const [tab, setTab] = useState<IssuesTab>('issues');

  const { activeIssues, doneIssues, activeTasks, doneTasks } = useMemo(() => {
    const activeIssues: WrkthruIssue[] = [];
    const doneIssues: WrkthruIssue[] = [];
    for (const issue of payload.issues) {
      (doneIssueIds.has(issue.id) ? doneIssues : activeIssues).push(issue);
    }
    const activeTasks: WrkthruTask[] = [];
    const doneTasks: WrkthruTask[] = [];
    for (const task of payload.tasks) {
      const isDone = task.status === 'Completed' || doneTaskIds.has(task.id);
      (isDone ? doneTasks : activeTasks).push(task);
    }
    return { activeIssues, doneIssues, activeTasks, doneTasks };
  }, [payload.issues, payload.tasks, doneIssueIds, doneTaskIds]);

  const activeCount = activeIssues.length + activeTasks.length;
  const doneCount = doneIssues.length + doneTasks.length;

  const visibleIssues = tab === 'issues' ? activeIssues : doneIssues;
  const visibleTasks = tab === 'issues' ? activeTasks : doneTasks;

  const issuesByBucket = useMemo(() => {
    const m = new Map<PriorityBucket, WrkthruIssue[]>();
    BUCKET_ORDER.forEach(b => m.set(b, []));
    for (const i of visibleIssues) m.get(i.bucket)?.push(i);
    for (const list of m.values()) {
      list.sort(byRankInBucket);
    }
    return m;
  }, [visibleIssues]);

  const tasksByBucket = useMemo(() => {
    const m = new Map<PriorityBucket, WrkthruTask[]>();
    TASK_ALLOWED_BUCKETS.forEach(b => m.set(b, []));
    for (const t of visibleTasks) {
      // Coerce P5 stragglers to P3 rather than dropping them.
      const b: PriorityBucket | null =
        t.bucket && TASK_ALLOWED_BUCKETS.includes(t.bucket) ? t.bucket : 'P3';
      m.get(b)?.push(t);
    }
    for (const list of m.values()) {
      list.sort(byRankInBucket);
    }
    return m;
  }, [visibleTasks]);

  return (
    // h-full so the header block stays fixed and only the section stack scrolls
    <div className="mx-auto flex h-full max-w-4xl flex-col p-6">
      <div className="shrink-0 space-y-5 pb-5">
        <WrkthruHeader
          date={payload.date}
          assignment={payload.assignment}
          operatorName={payload.operator.name}
        />
        {switcher}
        <PortfolioStatusBar
          counts={derivePortfolioStatus(payload.portfolioStatus, activeIssues, activeTasks)}
        />

        <IssuesTabs
          active={tab}
          issuesCount={activeCount}
          doneCount={doneCount}
          onChange={setTab}
        />
      </div>

      {/* sticky section headers pin to this scroll container */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {BUCKET_ORDER.map(b => {
          const issueList = issuesByBucket.get(b) ?? [];
          const taskList = TASK_ALLOWED_BUCKETS.includes(b) ? (tasksByBucket.get(b) ?? []) : [];
          const totalCount = issueList.length + taskList.length;
          return (
            <IssueSection key={b} bucket={b} count={totalCount}>
              {issueList.map(i => (
                <IssueCard
                  key={i.id}
                  issue={i}
                  onWrkthruIt={onWrkthruIt}
                  onMarkDone={
                    tab === 'issues' && onMarkDone ? () => onMarkDone('issue', i.id) : undefined
                  }
                  onUndone={tab === 'done' && onUndone ? () => onUndone('issue', i.id) : undefined}
                />
              ))}
              {taskList.map(t => (
                <TaskCard
                  key={t.id}
                  task={t}
                  onWrkthruIt={onWrkthruIt}
                  onMarkDone={
                    tab === 'issues' && onMarkDone ? () => onMarkDone('task', t.id) : undefined
                  }
                  // A Zoho-completed task has nothing to toggle: no row to delete,
                  // and un-marking wouldn't move it out of Done.
                  onUndone={
                    tab === 'done' && onUndone && t.status !== 'Completed' && doneTaskIds.has(t.id)
                      ? () => onUndone('task', t.id)
                      : undefined
                  }
                />
              ))}
            </IssueSection>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Classify each unique portfolio entity by the worst (lowest P-number) bucket of
 * any active task or issue tied to it, keeping the operator's full portfolio
 * scope (`base.total`) as the denominator. Issues key on `entityHref`, tasks on
 * `relatedCampaignHref`.
 */
function derivePortfolioStatus(
  base: PortfolioStatusCounts,
  issues: WrkthruIssue[],
  tasks: WrkthruTask[]
): PortfolioStatusCounts {
  const rank: Record<PriorityBucket, number> = { P1: 1, P2: 2, P3: 3, P4: 4, P5: 5 };
  const worst = new Map<string, PriorityBucket>();
  const consider = (key: string, bucket: PriorityBucket | null | undefined) => {
    if (!bucket || !key) return;
    const current = worst.get(key);
    if (!current || rank[bucket] < rank[current]) {
      worst.set(key, bucket);
    }
  };
  for (const i of issues) consider(i.entityHref || i.entityName, i.bucket);
  for (const t of tasks) consider(t.relatedCampaignHref || t.relatedCampaignName, t.bucket);

  const counts: Record<PriorityBucket, number> = { P1: 0, P2: 0, P3: 0, P4: 0, P5: 0 };
  for (const b of worst.values()) counts[b]++;

  const classified = counts.P1 + counts.P2 + counts.P3 + counts.P4 + counts.P5;
  const total = Math.max(base.total, classified);
  const unknown = Math.max(0, total - classified);

  return { ...base, total, counts, unknown };
}
